import { IDBPTransaction, openDB, type IDBPDatabase } from 'idb';

interface StoreSchema {
  [key: string]: unknown;
}

type StoreNames<T extends StoreSchema> = keyof T & string;

type StoreConfig<T extends StoreSchema> = {
  readonly [K in StoreNames<T>]: {
    keyPath?: string;
    autoIncrement?: boolean;
    indexes?: Record<string, { unique?: boolean; multiEntry?: boolean }>;
  };
};

export class IndexedDBManager<T extends StoreSchema> {
  constructor(
    private readonly config: {
      dbName: string;
      stores: StoreConfig<T>;
      version?: number;
      onUpgrade?: (
        db: IDBPDatabase,
        oldVersion: number,
        newVersion: number,
        transaction: IDBPTransaction<unknown, string[], 'versionchange'>,
      ) => void;
    },
  ) {}

  private db: Promise<IDBPDatabase> | null = null;

  getDatabase(): Promise<IDBPDatabase> {
    if (!this.db) {
      this.db = openDB(this.config.dbName, this.config.version ?? 1, {
        upgrade: async (db, oldVersion, newVersion, transaction) => {
          for (const [storeName, storeConfig] of Object.entries(this.config.stores)) {
            if (!db.objectStoreNames.contains(storeName)) {
              const store = db.createObjectStore(storeName, {
                keyPath: storeConfig.keyPath,
                autoIncrement: storeConfig.autoIncrement,
              });

              // Create indexes if specified
              if (storeConfig.indexes) {
                for (const [indexName, indexConfig] of Object.entries(storeConfig.indexes)) {
                  store.createIndex(indexName, indexName, {
                    unique: indexConfig.unique,
                    multiEntry: indexConfig.multiEntry,
                  });
                }
              }
            }
          }

          if (this.config.onUpgrade) {
            await this.config.onUpgrade(db, oldVersion, newVersion ?? 1, transaction);
          }
        },
      });
    }
    return this.db;
  }

  async getValue<K extends StoreNames<T>>(storeName: K, key: string): Promise<T[K] | null> {
    try {
      const db = await this.getDatabase();
      const val = await db.get(storeName, key);
      return val ?? null;
    } catch (error) {
      console.warn(`IndexedDB get failed for store ${storeName}:`, error);
      return null;
    }
  }

  async setValue<K extends StoreNames<T>>(storeName: K, key: string, val: T[K]): Promise<void> {
    try {
      const db = await this.getDatabase();
      await db.put(storeName, val, key);
    } catch (error) {
      console.warn(`IndexedDB set failed for store ${storeName}:`, error);
    }
  }

  async deleteValue<K extends StoreNames<T>>(storeName: K, key: string): Promise<void> {
    try {
      const db = await this.getDatabase();
      await db.delete(storeName, key);
    } catch (error) {
      console.warn(`IndexedDB delete failed for store ${storeName}:`, error);
    }
  }

  async deleteAll<K extends StoreNames<T>>(storeName: K): Promise<void> {
    try {
      const db = await this.getDatabase();
      await db.clear(storeName);
    } catch (error) {
      console.warn(`IndexedDB delete all failed for store ${storeName}:`, error);
    }
  }

  async getAllKeys<K extends StoreNames<T>>(storeName: K): Promise<string[]> {
    try {
      const db = await this.getDatabase();
      return (await db.getAllKeys(storeName)) as string[];
    } catch (error) {
      console.warn(`IndexedDB getAllKeys failed for store ${storeName}:`, error);
      return [];
    }
  }

  async getAllValues<K extends StoreNames<T>>(storeName: K): Promise<Array<T[K]>> {
    try {
      const db = await this.getDatabase();
      return await db.getAll(storeName);
    } catch (error) {
      console.warn(`IndexedDB getAllValues failed for store ${storeName}:`, error);
      return [];
    }
  }

  async close(): Promise<void> {
    if (this.db) {
      const database = await this.db;
      database.close();
      this.db = null;
    }
  }
}
